import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const CartSection = () => {
  const { cart, removeFromCart, updateQuantity, cartSubtotal, totalShipping, cartTotal, taxes, taxCalculation } = useCart();
  
  const hasExclusiveTax = taxCalculation?.exclusive > 0;

  return (
    <section className='cart py-80'>
      <div className='container container-lg'>
        <div className='row gy-4'>
          <div className='col-xl-9 col-lg-8'>
            <div className='cart-table border border-gray-100 rounded-16 overflow-hidden shadow-sm'>
              <div className='overflow-x-auto scroll-sm scroll-sm-horizontal'>
                {cart.length > 0 ? (
                  <table className='table rounded-8 overflow-hidden mb-0'>
                    <thead>
                      <tr className='bg-gray-50 border-bottom border-neutral-100'>
                        <th className='h6 mb-0 text-lg fw-bold px-16 py-12 border-end border-neutral-100 text-center'>Delete</th>
                        <th className='h6 mb-0 text-lg fw-bold px-16 py-12 border-end border-neutral-100 text-center'>Product Name</th>
                        <th className='h6 mb-0 text-lg fw-bold px-16 py-12 border-end border-neutral-100 text-center'>Price</th>
                        <th className='h6 mb-0 text-lg fw-bold px-16 py-12 border-end border-neutral-100 text-center'>Quantity</th>
                        <th className='h6 mb-0 text-lg fw-bold px-16 py-12 text-center'>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((item) => (
                        <tr key={`${item.id}-${item.variantId}`} className='border-bottom border-gray-100'>
                          <td className='px-16 py-12 border-end border-neutral-100 text-center'>
                            <button
                              onClick={() => removeFromCart(item.id, item.variantId)}
                              type='button'
                              className='remove-tr-btn d-flex align-items-center justify-content-center gap-12 text-gray-400 hover-text-danger-600 transition-2 w-100'
                            >
                              <i className='ph ph-x-circle text-2xl' />
                            </button>
                          </td>
                          <td className='px-16 py-12 border-end border-neutral-100'>
                            <div className='table-product d-flex align-items-center gap-24'>
                              <Link
                                to={`/product-details?id=${item.id}`}
                                className='table-product__thumb border border-gray-100 rounded-12 overflow-hidden flex-center p-0 bg-gray-50'
                              >
                                <img src={item.image || "assets/images/thumbs/product-img1.png"} alt={item.name} style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                              </Link>
                              <div className='table-product__content text-start'>
                                <h6 className='title text-md fw-bold mb-4'>
                                  <Link
                                    to={`/product-details?id=${item.id}`}
                                    className='link text-line-1 hover-text-main-600'
                                  >
                                    {item.name}
                                  </Link>
                                </h6>
                                <div className="flex-align gap-8 flex-wrap">
                                  {item.variantName && (
                                    <span className="badge bg-gray-100 text-gray-600 rounded-pill px-10 py-4 text-xs fw-medium">Variant: {item.variantName}</span>
                                  )}
                                  {item.taxId && (
                                    <span className="text-xs text-gray-400">
                                      {taxes.find(t => t.id === item.taxId)?.taxType === "INCLUSIVE" ? "Tax Incl." : "Tax Excl."}
                                    </span>
                                  )}
                                  <span className={`text-xs fw-bold ${item.stock === 0 ? 'text-danger-600' : 'text-success-600'}`}>
                                    {item.stock === 0 ? 'Out of Stock' : 'In Stock'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className='px-16 py-12 border-end border-neutral-100 text-center'>
                            <span className='text-md fw-bold text-gray-800'>
                              Rs.{item.price}
                            </span>
                          </td>
                          <td className='px-16 py-12 border-end border-neutral-100 text-center'>
                            <div className='d-inline-flex rounded-12 overflow-hidden border border-gray-100 shadow-sm bg-white mx-auto'>
                              <button
                                onClick={() => updateQuantity(item.id, item.variantId, item.quantity - 1)}
                                type='button'
                                className='h-36 w-36 text-neutral-600 bg-gray-50 flex-center hover-bg-main-600 hover-text-white transition-2'
                              >
                                <i className='ph ph-minus' />
                              </button>
                              <input
                                type='number'
                                className='h-36 w-48 text-center border-0 fw-bold bg-transparent'
                                style={{ outline: 'none' }}
                                value={item.quantity}
                                readOnly
                              />
                              <button
                                onClick={() => updateQuantity(item.id, item.variantId, item.quantity + 1)}
                                type='button'
                                disabled={item.quantity >= item.stock}
                                className={`h-36 w-36 text-neutral-600 flex-center transition-2 ${item.quantity >= item.stock ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50 hover-bg-main-600 hover-text-white'}`}
                              >
                                <i className='ph ph-plus' />
                              </button>
                            </div>
                          </td>
                          <td className='px-16 py-12 text-center'>
                            <span className='text-md fw-bold text-main-600'>
                              Rs.{(item.price * item.quantity).toFixed(0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-80 bg-gray-50 m-24 rounded-16">
                    <i className="ph ph-shopping-cart text-6xl text-gray-300 mb-16"></i>
                    <h5 className="text-gray-500">Your cart is empty.</h5>
                    <Link to="/shop" className="btn btn-main-600 rounded-12 px-32 py-12 mt-16">Start Shopping</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='col-xl-3 col-lg-4'>
            <div className='cart-sidebar border border-gray-100 rounded-16 px-24 py-32 bg-white shadow-sm'>
              <h6 className='text-xl mb-24 fw-bold'>Order Summary</h6>
              <div className='bg-gray-50 rounded-12 p-24 mb-24'>
                <div className='mb-16 flex-between gap-8'>
                  <span className='text-gray-600 fw-medium'>Subtotal</span>
                  <span className='text-gray-900 fw-bold'>Rs.{cartSubtotal.toFixed(0)}</span>
                </div>
                
                {hasExclusiveTax && (
                  <div className='mb-16 flex-between gap-8'>
                    <span className='text-gray-600 fw-medium'>GST</span>
                    <span className='text-gray-900 fw-bold'>Rs.{taxCalculation.exclusive.toFixed(0)}</span>
                  </div>
                )}

                <div className='mb-0 flex-between gap-8'>
                  <span className='text-gray-600 fw-medium'>Shipping</span>
                  <span className={totalShipping === 0 ? 'text-success-600 fw-bold' : 'text-gray-900 fw-bold'}>
                    {totalShipping === 0 ? 'FREE' : `Rs.${totalShipping.toFixed(0)}`}
                  </span>
                </div>
              </div>
              <div className='border-top border-gray-100 pt-24 mb-32'>
                <div className='flex-between gap-8'>
                  <span className='text-gray-900 text-xl fw-bold'>Total</span>
                  <span className='text-main-600 text-2xl fw-bold'>
                    Rs.{cartTotal.toFixed(0)}
                  </span>
                </div>
              </div>
              <Link
                to='/checkout'
                className={`btn bg-main-600 text-white py-16 w-100 rounded-12 fw-bold hover-bg-main-700 shadow-sm transition-2 ${cart.length === 0 ? 'disabled pointer-events-none opacity-50' : ''}`}
              >
                Proceed to Checkout
              </Link>
              <Link to="/shop" className="btn w-100 mt-16 text-gray-500 fw-medium hover-text-main-600 transition-2 text-sm">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CartSection;